import axios from 'axios';

/**
 * OpenRouter Client for unified LLM access
 * Supports: GPT-4, Claude, Gemini, DeepSeek, and more
 *
 * Usage:
 * - Free tier: google/gemini-2.0-flash-exp (defaults to :online automatically)
 * - Premium tier: anthropic/claude-3.5-sonnet or openai/gpt-4o
 */
class OpenRouterClient {
  constructor(apiKey) {
    const env = typeof import.meta !== 'undefined' && import.meta?.env
      ? import.meta.env
      : (typeof process !== 'undefined' && process.env) || {};

    this.apiKey = apiKey || env.VITE_OPENROUTER_API_KEY;
    this.baseURL = 'https://openrouter.ai/api/v1';

    if (!this.apiKey) {
      console.warn('⚠️ OpenRouter API key not found. Set VITE_OPENROUTER_API_KEY in .env');
    }
  }

  getModelForTier(userTier = 'free') {
    const env = typeof import.meta !== 'undefined' && import.meta?.env
      ? import.meta.env
      : (typeof process !== 'undefined' && process.env) || {};

    const models = {
      free: env.VITE_OPENROUTER_MODEL_FREE || 'google/gemini-3-flash-preview',
      premium: env.VITE_OPENROUTER_MODEL_PREMIUM || 'anthropic/claude-3.5-sonnet',
      enterprise: env.VITE_OPENROUTER_MODEL_ENTERPRISE || 'openai/gpt-4o'
    };

    const baseModel = models[userTier] || models.free;
    return this.appendOnlineSuffix(baseModel);
  }

  appendOnlineSuffix(model) {
    if (!model || model.includes(':online')) return model;

    const [, modelName = ''] = model.split('/');
    if (modelName.includes(':')) {
      return model; // already has a qualifier like ":free"
    }

    return `${model}:online`;
  }

  buildPayload({ model, prompt, messages, userTier, options = {}, stream = false }) {
    const {
      temperature,
      maxTokens,
      topP,
      plugins = [],
      webSearchOptions,
      ...restOptions
    } = options;

    const payload = {
      model,
      messages: messages || [{ role: 'user', content: prompt }],
      temperature: temperature ?? 0.7,
      max_tokens: maxTokens ?? (userTier === 'free' ? 1024 : 2048),
      top_p: topP ?? 0.9,
      stream,
      ...restOptions
    };

    if (!model.includes(':online')) {
      payload.plugins = [...plugins, { id: 'web' }];
    } else if (plugins.length) {
      payload.plugins = plugins;
    }

    if (webSearchOptions) {
      payload.web_search_options = webSearchOptions;
    }

    if (!stream) {
      delete payload.stream;
    }

    return payload;
  }

  async generateText(prompt, userTier = 'free', options = {}) {
    if (!this.apiKey) {
      throw new Error('OpenRouter API key is required');
    }

    try {
      const model = this.getModelForTier(userTier);
      const payload = this.buildPayload({ model, prompt, userTier, options });

      const response = await axios.post(
        `${this.baseURL}/chat/completions`,
        payload,
        {
          headers: {
            'Authorization': `Bearer ${this.apiKey}`,
            'HTTP-Referer': typeof window !== 'undefined' ? window.location.origin : 'https://aspirant-ai.com',
            'X-Title': 'JobAspirant',
            'Content-Type': 'application/json'
          }
        }
      );

      return response.data.choices[0].message.content;
    } catch (error) {
      console.error('❌ OpenRouter API Error:', error.response?.data || error.message);
      throw new Error(this.handleError(error));
    }
  }

  async streamText(prompt, userTier = 'free', onChunk, options = {}) {
    if (!this.apiKey) {
      throw new Error('OpenRouter API key is required');
    }

    try {
      const model = this.getModelForTier(userTier);
      const payload = this.buildPayload({ model, prompt, userTier, options, stream: true });

      const response = await fetch(`${this.baseURL}/chat/completions`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${this.apiKey}`,
          'HTTP-Referer': typeof window !== 'undefined' ? window.location.origin : 'https://aspirant-ai.com',
          'X-Title': 'JobAspirant',
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(payload),
        signal: options.signal
      });

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      const reader = response.body.getReader();
      const decoder = new TextDecoder('utf-8');

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        const chunk = decoder.decode(value);
        const lines = chunk.split('\n').filter(line => line.trim() !== '');

        for (const line of lines) {
          if (line.startsWith('data: ')) {
            const data = line.slice(6);
            if (data === '[DONE]') continue;

            try {
              const json = JSON.parse(data);
              const content = json.choices?.[0]?.delta?.content;
              if (content && onChunk) {
                onChunk(content);
              }
            } catch (e) {
              console.warn('⚠️ Skipped malformed chunk:', e.message);
            }
          }
        }
      }
    } catch (error) {
      console.error('❌ OpenRouter Streaming Error:', error);
      throw new Error(this.handleError(error));
    }
  }

  async chatWithHistory(messages, userTier = 'free', options = {}) {
    if (!this.apiKey) {
      throw new Error('OpenRouter API key is required');
    }

    try {
      const model = this.getModelForTier(userTier);
      const formattedMessages = messages.map(msg => ({
        role: msg.role === 'model' ? 'assistant' : msg.role,
        content: typeof msg.parts === 'string'
          ? msg.parts
          : Array.isArray(msg.parts)
            ? msg.parts.map(p => p.text || '').join('')
            : msg.parts?.[0]?.text || ''
      }));

      const payload = this.buildPayload({
        model,
        userTier,
        options,
        messages: formattedMessages
      });

      const response = await axios.post(
        `${this.baseURL}/chat/completions`,
        payload,
        {
          headers: {
            'Authorization': `Bearer ${this.apiKey}`,
            'HTTP-Referer': typeof window !== 'undefined' ? window.location.origin : 'https://aspirant-ai.com',
            'X-Title': 'JobAspirant',
            'Content-Type': 'application/json'
          }
        }
      );

      const responseText = response.data.choices[0].message.content;

      return {
        response: responseText,
        updatedHistory: [
          ...messages,
          { role: 'assistant', parts: [{ text: responseText }] }
        ]
      };
    } catch (error) {
      console.error('❌ OpenRouter Chat Error:', error.response?.data || error.message);
      throw new Error(this.handleError(error));
    }
  }

  async analyzeResume(resumeText, userTier = 'free', preferredRoles = [], experienceYears = 0) {
    const prompt = `You are an expert resume analyzer. Extract skills and provide feedback in STRICT JSON format.

Resume Text:
${resumeText}

Extract ALL skills mentioned. Return ONLY this JSON structure (no markdown, no comments):
{
  "overall_score": 85,
  "skills": [
    {"name": "JavaScript", "category": "technical", "proficiency": "advanced", "confidence_score": 0.9, "years_experience": 3, "is_technical": true}
  ],
  "feedback": [
    {"category": "skills", "feedback_text": "Strong technical foundation", "priority_level": "high", "improvement_suggestions": ["Learn TypeScript", "Add more projects"]}
  ]
}

RULES:
1. Extract ALL technical skills (languages, frameworks, tools, databases, cloud, APIs, CS concepts)
2. Extract soft skills (problem solving, leadership, etc.)
3. Include skills from: Technical Skills section, Projects, Education, Experience
4. Proficiency: "beginner", "intermediate", "advanced", or "expert"
5. Category: "technical", "soft", or "domain"
6. Return ONLY valid JSON - no extra text before or after

CRITICAL: The JSON must be perfectly valid. Double-check all commas and brackets.`;

    try {
      console.log('🔍 Analyzing resume via OpenRouter...');
      const responseText = await this.generateText(prompt, userTier, {
        temperature: 0.2,
        maxTokens: 4096
      });

      console.log('📝 Raw response length:', responseText.length);

      let analysis = null;

      const codeBlockMatch = responseText.match(/```(?:json)?\s*(\{[\s\S]*?\})\s*```/);
      if (codeBlockMatch) {
        console.log('✅ Found JSON in code block');
        try {
          analysis = JSON.parse(codeBlockMatch[1]);
        } catch (e) {
          console.warn('⚠️ Code block JSON parse failed, trying repair...');
        }
      }

      if (!analysis) {
        let jsonMatch = responseText.match(/\{[\s\S]*\}/);
        if (jsonMatch) {
          let jsonText = jsonMatch[0];

          jsonText = jsonText
            .replace(/,(\s*[\]}])/g, '$1')
            .replace(/(['"])?([a-zA-Z0-9_]+)(['"])?\s*:/g, '"$2":')
            .replace(/:\s*'([^']*)'/g, ': "$1"')
            .replace(/\/\/.*$/gm, '')
            .replace(/\/\*[\s\S]*?\*\//g, '')
            .replace(/,\s*$/g, '')
            .replace(/\{\s*"[^"]*"\s*:\s*"[^"]*$/g, '');

          const openBraces = (jsonText.match(/\{/g) || []).length;
          const closeBraces = (jsonText.match(/\}/g) || []).length;
          const openBrackets = (jsonText.match(/\[/g) || []).length;
          const closeBrackets = (jsonText.match(/\]/g) || []).length;

          jsonText += ']'.repeat(Math.max(0, openBrackets - closeBrackets));
          jsonText += '}'.repeat(Math.max(0, openBraces - closeBraces));

          try {
            analysis = JSON.parse(jsonText);
            console.log('✅ Parsed JSON after repair');
          } catch (e) {
            console.error('❌ JSON parse error:', e.message);
            console.log('Failed JSON (first 500 chars):', jsonText.substring(0, 500));
            console.log('Failed JSON (last 500 chars):', jsonText.substring(jsonText.length - 500));
            throw e;
          }
        }
      }

      if (!analysis) {
        throw new Error('No valid JSON found in response');
      }

      console.log(`✅ Extracted ${analysis.skills?.length || 0} skills from resume`);
      return this.validateAnalysisResponse(analysis);

    } catch (error) {
      console.error('❌ Resume Analysis Error:', error);

      if (error.message.includes('JSON') || error.message.includes('parse')) {
        throw new Error('AI returned invalid data format. Please try uploading again or contact support.');
      }

      throw new Error('Failed to analyze resume. Please try again.');
    }
  }

  validateAnalysisResponse(analysis) {
    const defaultResponse = {
      overall_score: 0,
      skills: [],
      feedback: [],
      job_matches: []
    };

    if (!analysis || typeof analysis !== 'object') {
      return defaultResponse;
    }

    return {
      overall_score: Math.max(0, Math.min(100, analysis.overall_score || 0)),
      skills: Array.isArray(analysis.skills) ? analysis.skills.map(skill => ({
        name: skill.name || 'Unknown Skill',
        category: ['technical', 'soft', 'domain'].includes(skill.category) ? skill.category : 'technical',
        proficiency: ['beginner', 'intermediate', 'advanced', 'expert'].includes(skill.proficiency) ? skill.proficiency : 'intermediate',
        confidence_score: Math.max(0, Math.min(1, skill.confidence_score || 0.5)),
        years_experience: Math.max(0, skill.years_experience || 0),
        is_technical: Boolean(skill.is_technical)
      })) : [],
      feedback: Array.isArray(analysis.feedback) ? analysis.feedback.map(feedback => ({
        category: feedback.category || 'content',
        feedback_text: feedback.feedback_text || 'No feedback provided',
        priority_level: ['low', 'medium', 'high'].includes(feedback.priority_level) ? feedback.priority_level : 'medium',
        improvement_suggestions: Array.isArray(feedback.improvement_suggestions) ? feedback.improvement_suggestions : []
      })) : [],
      job_matches: []
    };
  }

  handleError(error) {
    const errorData = error.response?.data;

    if (error.response?.status === 429) {
      return 'Rate limit exceeded. Please wait a moment before trying again.';
    }

    if (error.response?.status === 401) {
      return 'Invalid API key. Please check your OpenRouter configuration.';
    }

    if (error.response?.status === 402) {
      return 'Insufficient credits. Please add credits to your OpenRouter account.';
    }

    if (errorData?.error?.message) {
      return errorData.error.message;
    }

    return 'An unexpected error occurred. Please try again.';
  }
}

const openRouterClient = new OpenRouterClient();

export default openRouterClient;
export { OpenRouterClient };
